/**
 * @graphql agg
 */

import { useEffect, useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { expVal } from '@confluence/feature-experiments';

import type {
	GraphQLContentStatus,
	SitePermissionType,
} from './__generated__/useAllCommentsDataQuery.graphql.ts';
import type {
	useCommentByIdQuery$data,
	useCommentByIdQuery$variables,
} from './__generated__/useCommentByIdQuery.graphql.ts';
import {
	type Comment,
	type CommentReply,
	type FetchPolicies,
	allContentStatuses,
} from './useAllCommentsData.ts';
import { findAndFilterComment } from './commentDataManagerUtils.ts';
import { useProcessedCommentsDataState } from './useProcessedCommentsData.ts';

// The `useCommentById` hook efficiently fetches a single comment by ID with a simple strategy:
// First, it checks the cache for the allCommentsDataQuery using useAllCommentsData with cache-only
// policy. If the comment is found in the cache and data is processed, it uses that data. Otherwise,
// it makes a separate query for just the specific comment. This ensures quick access to individual
// comments without unnecessary network requests when data is already cached. The hook also provides
// loading states, error handling, and comprehensive type safety.

// Unfortunately our Relay-friendly type generation tools don't support GQL fragments yet - but in
// order to keep the definition of the comment fields in sync (so the cache update fragment is
// always in sync with the full query) we will need to manually make any changes to one definition
// in any others as well, until our tooling supports fragments and then we move to a more
// fragmented-based approach.  Note there are also comment queries or fragments in the following
// files that need to be kept in sync as well:
// - next/packages/comments-data-manager/src/useAllCommentsData.ts
// - next/packages/comments-data-manager/src/updateCommentInCache.ts
// - next/packages/comments-data-manager/src/useCreateObjectCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineReplyMutation.ts
// - next/packages/object-comments/src/components/ObjectCommentContainer.tsx

// TODO: Replace with the exported fragment from useAllCommentsData.ts when our tooling supports
// it, if we are allowed to share fragments between packages/files.  If we are not allowed to share
// fragments, we should move this hook - or at least the part that executes the query into the
// useAllCommentsData.ts file.
const commentByIdQuery = graphql`
	query useCommentByIdQuery(
		$pageId: ID!
		$commentId: ID
		$inlineMarkerRef: String
		$contentStatus: [GraphQLContentStatus]
	) {
		comments(
			pageId: $pageId
			contentStatus: $contentStatus
			commentId: $commentId
			inlineMarkerRef: $inlineMarkerRef
		) {
			nodes {
				id
				author {
					__typename
					displayName
					permissionType
					... on KnownUser {
						accountId
						permissionType
					}
					profilePicture {
						path
					}
					type
				}
				contributors {
					__typename
					displayName
					profilePicture {
						path
					}
					... on KnownUser {
						accountId
					}
				}
				body(representation: ATLAS_DOC_FORMAT) {
					value
				}
				container {
					id
				}
				contentStatus
				createdAt {
					value
				}
				createdAtNonLocalized
				links {
					editui
					webui
				}
				location {
					__typename
					type
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							isDangling
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
						inlineText
					}
				}
				parentId
				permissions {
					isEditable
					isRemovable
					isResolvable
				}
				reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
					ari
					containerAri
					reactionsCount
					reactionsSummaryForEmoji {
						id
						count
						emojiId
						reacted
					}
				}
				replies {
					id
					author {
						__typename
						displayName
						permissionType
						... on KnownUser {
							accountId
						}
						profilePicture {
							path
						}
					}
					contributors {
						__typename
						displayName
						profilePicture {
							path
						}
						... on KnownUser {
							accountId
						}
					}
					body(representation: ATLAS_DOC_FORMAT) {
						value
					}
					contentStatus
					createdAt {
						value
					}
					createdAtNonLocalized
					links {
						editui
						webui
					}
					location {
						__typename
						type
						... on FooterComment {
							commentResolveProperties {
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
						... on InlineComment {
							inlineMarkerRef
							inlineResolveProperties {
								isDangling
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
							inlineText
						}
					}
					parentId
					permissions {
						isEditable
						isRemovable
						isResolvable
					}
					reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
						ari
						containerAri
						reactionsCount
						reactionsSummaryForEmoji {
							id
							count
							emojiId
							reacted
						}
					}
					version {
						by {
							__typename
							displayName
						}
						number
						when
					}
				}
				version {
					by {
						__typename
						type
						displayName
						... on KnownUser {
							accountId
						}
					}
					friendlyWhen
					number
					when
				}
			}
		}
	}
`;

export type UseCommentByIdOptions = {
	fetchPolicy?: FetchPolicies;
	onError?: (error: Error) => void;
	skip?: boolean;
};

export type UseCommentByIdProps = {
	commentId?: string;
	markerRef?: string;
	contentId: string;
	contentStatus?: GraphQLContentStatus[];
	options?: UseCommentByIdOptions;
};

export type UseCommentByIdReturn = {
	loading: boolean;
	error: Error | undefined;
	comment: Comment | CommentReply | undefined;
};

/**
 * Error message used when a comment is not found after all data has loaded.
 * NOTE: This currently matches what the backend returns, but is not guaranteed to match in the future.
 * There is a separate case for this string in errorUtils, but it still risks getting out of date if used elsewhere.
 */
export const USE_COMMENT_BY_ID_COMMENT_NOT_FOUND_MESSAGE =
	'Comment not found: No comment found for the given id';

/**
 * Error message used when useCommentById is called with invalid parameters.
 * This error is returned (not thrown) when required parameters are missing.
 */
export const USE_COMMENT_BY_ID_VALIDATION_ERROR_MESSAGE =
	'useCommentById requires either commentId or markerRef and contentId to be provided';

/**
 * Custom React hook for loading a single comment by ID or markerRef
 *
 * Features:
 * - First checks the cache for allCommentsDataQuery using useAllCommentsData with cache-only policy
 * - If found in cache and data is processed, uses that data (no network request)
 * - If not found in cache, makes a separate query for the specific comment
 * - Provides loading states and error handling
 *
 * @param commentId - The ID of the comment to fetch (optional if markerRef is provided)
 * @param markerRef - The inline marker ref of the comment to fetch (optional if commentId is provided)
 * @param options - Configuration options for the hook
 * @returns Object containing comment data, loading state, and utility functions
 */
export const useCommentById = ({
	commentId,
	markerRef,
	contentId,
	contentStatus = ['CURRENT'],
	options = {},
}: UseCommentByIdProps): UseCommentByIdReturn => {
	const {
		fetchPolicy = 'cache-first',
		onError: parentOnError,
		skip: explicitlySkipped = false,
	} = options;

	// Skip queries if experiment is disabled or explicitly skipped through options
	const skip =
		explicitlySkipped || !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false);

	// Get processed comments data for new comments data manager
	const processedCommentsData = useProcessedCommentsDataState();

	// Validate that at least one identifier is provided (only if not skipping)
	const validationError = useMemo(
		() =>
			!skip && (!contentId || (!commentId && !markerRef))
				? new Error(USE_COMMENT_BY_ID_VALIDATION_ERROR_MESSAGE)
				: undefined,
		[skip, commentId, markerRef, contentId],
	);

	const skipQueries = skip || !!validationError;

	// Skip the individual comment query if we have cached data from the full query, it's been processed, and we're not asking for fresh BE data
	const shouldSkipOneCommentQuery =
		skipQueries || (fetchPolicy !== 'network-only' && processedCommentsData.isDataProcessed);

	// Always query with all 4 content statuses to guarantee cache hits on preloaded data
	// We'll filter the result client-side based on the passed-in contentStatus
	const oneCommentData = useQueryInterop<useCommentByIdQuery$data, useCommentByIdQuery$variables>(
		commentByIdQuery,
		{
			variables: {
				pageId: contentId ?? '',
				commentId: commentId || undefined,
				inlineMarkerRef: markerRef || undefined,
				contentStatus: allContentStatuses,
			},
			fetchPolicy,
			skip: shouldSkipOneCommentQuery,
			onError: (error: Error) => {
				// Handle specific GraphQL errors gracefully
				// WS-3494 - If the error isn't related to reactionsSummary returning null, just mark it handled
				// COMMENTS-188 - Or if the error isn't related to cc-pages not being reachable just mark it handled
				if (
					error?.message?.search(/reactions/i) !== -1 ||
					error?.message?.search(/cc-pages/i) !== -1
				) {
					markErrorAsHandled(error);
				}
			},
		},
	);

	// Look for the comment using the flat map for O(1) lookup, or fall back to searching arrays
	const finalComment = useMemo(() => {
		if (validationError) {
			return undefined;
		}

		// If data is processed, try to find in the flat maps (fastest - O(1))
		if (processedCommentsData.isDataProcessed) {
			if (commentId) {
				const cachedComment = processedCommentsData.flatCommentsMap?.[commentId];
				if (cachedComment) {
					return cachedComment;
				}
			} else if (markerRef) {
				const cachedCommentByMarkerRef = processedCommentsData.flatMarkerRefMap?.[markerRef];
				if (cachedCommentByMarkerRef) {
					return cachedCommentByMarkerRef;
				}
			}
		}

		// Fall back to searching in oneCommentData if available
		if (oneCommentData.data?.comments?.nodes) {
			// Filter out any null nodes - can't just use .filter because TS complains about type
			// mismatch.  Fortunately, this query should only have one comment node anyway.
			const commentData = oneCommentData.data.comments.nodes
				.map((node) => node)
				.filter((node) => node);

			if (!commentData) {
				return undefined;
			}

			// Search in retrieved comments and their replies
			return findAndFilterComment(commentData, commentId, markerRef, contentStatus);
		}

		return undefined;
	}, [
		validationError,
		commentId,
		processedCommentsData.flatCommentsMap,
		processedCommentsData.flatMarkerRefMap,
		processedCommentsData.isDataProcessed,
		oneCommentData.data?.comments?.nodes,
		markerRef,
		contentStatus,
	]);

	// Create an error if we have the full response but can't find the comment
	// 99% of the time this error will be thrown when cachedUseAllCommentsData is populated, but the comment isn't available
	// useCommentByIdQuery **should** throw a similar error if the comment isn't found from the BE,
	// but keep the check here in case the BE changes, or our comment is in another status & is filtered out
	const commentNotFoundError = useMemo(() => {
		// Only create error if:
		// 1. We're not loading anymore (have full response)
		// 2. We have data (either from processedCommentsData or oneCommentData)
		// 3. We searched for the comment but didn't find it
		// 4. There's no existing error
		if (
			!skipQueries &&
			processedCommentsData.isDataProcessed &&
			!oneCommentData.loading &&
			!processedCommentsData.error &&
			!oneCommentData.error &&
			finalComment === undefined
		) {
			return new Error(USE_COMMENT_BY_ID_COMMENT_NOT_FOUND_MESSAGE);
		}
		return undefined;
	}, [
		skipQueries,
		processedCommentsData.isDataProcessed,
		processedCommentsData.error,
		oneCommentData.loading,
		oneCommentData.error,
		finalComment,
	]);

	// Memoize the final error to ensure stable reference for use in external useEffects
	const error = useMemo(() => {
		return (
			validationError || processedCommentsData.error || oneCommentData.error || commentNotFoundError
		);
	}, [validationError, processedCommentsData.error, oneCommentData.error, commentNotFoundError]);

	// Call onError when the error changes
	useEffect(() => {
		if (error && parentOnError) {
			parentOnError(error);
		}
	}, [error, parentOnError]);

	return {
		loading: !processedCommentsData.isDataProcessed && oneCommentData.loading, // (Only loading state if both are loading)
		error,
		comment: finalComment,
	};
};

export type FetchCommentByIdProps = {
	commentId?: string;
	markerRef?: string;
	contentId: string;
	fetchPolicy?: 'cache-first' | 'network-only' | 'cache-only' | 'no-cache';
};

/**
 * Fetches a single comment by ID or markerRef by executing the GraphQL query.
 * This function can be used for preloading comment data outside of React components.
 *
 * @param commentId - The ID of the comment to fetch (optional if markerRef is provided)
 * @param markerRef - The inline marker ref of the comment to fetch (optional if commentId is provided)
 * @param contentId - The ID of the content/page
 * @param fetchPolicy - Optional fetch policy (defaults to 'network-only')
 * @returns Promise that resolves to the comment data
 */
export const fetchCommentById = async ({
	commentId,
	markerRef,
	contentId,
	fetchPolicy = 'network-only',
}: FetchCommentByIdProps): Promise<
	| {
			readonly author: {
				readonly __typename: string;
				readonly accountId?: string | null;
				readonly displayName: string | null;
				readonly permissionType: SitePermissionType | null;
				readonly profilePicture: {
					readonly path: string;
				} | null;
				readonly type: string | null;
			};
			readonly body: {
				readonly value: string;
			};
			readonly container: {
				readonly id: string | null;
			};
			readonly contentStatus: string;
			readonly contributors: ReadonlyArray<{
				readonly __typename: string;
				readonly accountId?: string | null;
				readonly displayName: string | null;
				readonly profilePicture: {
					readonly path: string;
				} | null;
			} | null> | null;
			readonly createdAt: {
				readonly value: string;
			};
			readonly createdAtNonLocalized: string;
			readonly id: string;
			readonly links: {
				readonly editui: string | null;
				readonly webui: string | null;
			};
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			readonly location: {
				readonly __typename: string;
				readonly commentResolveProperties?: {
					readonly resolved: boolean;
					readonly resolvedFriendlyDate: string | null;
					readonly resolvedTime: AGG$Long | null;
					readonly resolvedUser: string | null;
				} | null;
				readonly inlineMarkerRef?: string | null;
				readonly inlineResolveProperties?: {
					readonly isDangling: boolean;
					readonly resolved: boolean;
					readonly resolvedFriendlyDate: string | null;
					readonly resolvedTime: AGG$Long | null;
					readonly resolvedUser: string | null;
				} | null;
				readonly inlineText?: string | null;
				readonly type: string;
			};
			readonly parentId: string | null;
			readonly permissions: {
				readonly isEditable: boolean;
				readonly isRemovable: boolean;
				readonly isResolvable: boolean;
			};
			readonly reactionsSummary: {
				readonly ari: string;
				readonly containerAri: string;
				readonly reactionsCount: number;
				readonly reactionsSummaryForEmoji: ReadonlyArray<{
					readonly count: number;
					readonly emojiId: string;
					readonly id: string;
					readonly reacted: boolean;
				} | null>;
			} | null;
			readonly replies: ReadonlyArray<{
				readonly author: {
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly permissionType: SitePermissionType | null;
					readonly profilePicture: {
						readonly path: string;
					} | null;
				};
				readonly body: {
					readonly value: string;
				};
				readonly contentStatus: string;
				readonly contributors: ReadonlyArray<{
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly profilePicture: {
						readonly path: string;
					} | null;
				} | null> | null;
				readonly createdAt: {
					readonly value: string;
				};
				readonly createdAtNonLocalized: string;
				readonly id: string;
				readonly links: {
					readonly editui: string | null;
					readonly webui: string | null;
				};
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				readonly location: {
					readonly __typename: string;
					readonly commentResolveProperties?: {
						readonly resolved: boolean;
						readonly resolvedFriendlyDate: string | null;
						readonly resolvedTime: AGG$Long | null;
						readonly resolvedUser: string | null;
					} | null;
					readonly inlineMarkerRef?: string | null;
					readonly inlineResolveProperties?: {
						readonly isDangling: boolean;
						readonly resolved: boolean;
						readonly resolvedFriendlyDate: string | null;
						readonly resolvedTime: AGG$Long | null;
						readonly resolvedUser: string | null;
					} | null;
					readonly inlineText?: string | null;
					readonly type: string;
				};
				readonly parentId: string | null;
				readonly permissions: {
					readonly isEditable: boolean;
					readonly isRemovable: boolean;
					readonly isResolvable: boolean;
				};
				readonly reactionsSummary: {
					readonly ari: string;
					readonly containerAri: string;
					readonly reactionsCount: number;
					readonly reactionsSummaryForEmoji: ReadonlyArray<{
						readonly count: number;
						readonly emojiId: string;
						readonly id: string;
						readonly reacted: boolean;
					} | null>;
				} | null;
				readonly version: {
					readonly by: {
						readonly __typename: string;
						readonly displayName: string | null;
					} | null;
					readonly number: number | null;
					readonly when: string | null;
				};
			} | null>;
			readonly version: {
				readonly by: {
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly type: string | null;
				} | null;
				readonly friendlyWhen: string | null;
				readonly number: number | null;
				readonly when: string | null;
			};
	  }
	| null
	| undefined
> => {
	if (!contentId || (!commentId && !markerRef)) {
		return Promise.resolve(null);
	}

	const response = await queryInterop<useCommentByIdQuery$data>({
		query: commentByIdQuery,
		variables: {
			pageId: contentId,
			commentId: commentId || undefined,
			inlineMarkerRef: markerRef || undefined,
			// Query for all content statuses - don't filter by contentStatus
			contentStatus: allContentStatuses,
		},
		fetchPolicy,
	});

	const nodes = response?.data?.comments?.nodes;
	if (!nodes || nodes.length === 0) {
		return null;
	}

	// Filter out nulls and convert readonly array to mutable array
	const comments: Comment[] = nodes.filter((node): node is Comment => node !== null);
	return findAndFilterComment(comments, commentId, markerRef, allContentStatuses);
};
